import { AlertTriangle } from 'lucide-react'
import { RULES } from '@sdg/shared'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog'

/**
 * The two rules a student has to know before they walk off.
 *
 * Shown immediately after the registration celebration closes, and deliberately
 * styled as a warning rather than as more good news: the card before it is
 * confetti and a registration number, and anything that looks like part of
 * that gets dismissed with it. Red, a warning icon, and a button that says
 * "I understand" — because a student who misses this visits six booths and
 * then finds out they were one short of the draw.
 *
 * Both thresholds come from `RULES`, not from the text. They have already
 * moved once during build — the certificate went 14 → 12 → 14 — and a notice
 * that disagrees with the pass page is worse than no notice.
 *
 * The wording is the Committee's, kept close to what they wrote.
 */
export function ImportantNote({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        `showClose={false}` and no dismiss on the backdrop: this is the one
        card in the flow that must be read, and a stray tap outside it is how
        a student skips something without noticing they did.
      */}
      <DialogContent
        showClose={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="max-h-[90dvh] overflow-y-auto p-0"
      >
        <div className="flex flex-col px-6 pb-6 pt-7">
          <span className="mx-auto mb-3 grid size-14 shrink-0 place-items-center rounded-full bg-red-100 text-red-600">
            <AlertTriangle className="size-7" strokeWidth={2.3} />
          </span>

          <DialogTitle className="text-center text-red-700">Important Note</DialogTitle>

          <DialogDescription className="mt-1.5 text-center">
            Please read this before you start — it decides what you can claim at the end of
            the day.
          </DialogDescription>

          <ol className="mt-5 space-y-4">
            <li className="rounded-xl border border-red-200 bg-red-50/60 p-3.5">
              <p className="text-[13px] font-extrabold uppercase tracking-wide text-red-700">
                1. Lucky draw contest
              </p>
              <p className="mt-1 text-[13.5px] leading-relaxed text-ink">
                Please get your QR code scanned at least in{' '}
                <b className="text-red-700">{RULES.luckyDrawThreshold} booths</b> to be eligible
                for participation.
              </p>
            </li>

            <li className="rounded-xl border border-red-200 bg-red-50/60 p-3.5">
              <p className="text-[13px] font-extrabold uppercase tracking-wide text-red-700">
                2. Certificate of participation
              </p>
              <ul className="mt-1.5 space-y-2 text-[13.5px] leading-relaxed text-ink">
                <li className="flex gap-2">
                  <span className="shrink-0 font-bold text-red-700">a.</span>
                  <span>
                    Please get your QR code scanned in{' '}
                    <b className="text-red-700">all {RULES.certificateThreshold} booths</b> to be
                    eligible. Once all {RULES.certificateThreshold}  booths are scanned, you will get email notification to download your certificate of participation.
                    On clicking in the email link you will be asked to upload the below information in order to dowload your certificate of participation.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="shrink-0 font-bold text-red-700">b.</span>
                  <span>
                    Kindly post some pictures of yourself with being in the event in any of your
                    social media. Proof of posting needs to be submitted.
                  </span>
                </li>
              </ul>
            </li>
          </ol>

          <Button
            block
            size="lg"
            className="mt-5 bg-red-600 hover:bg-red-700"
            onClick={() => onOpenChange(false)}
            autoFocus
          >
            I understand
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
